import { useContext, useEffect, useState } from 'react'
import UserContext from '../../dataContext'
import Spinner from '../../Components/Spinner'
import "../../assets/scss/Billing/products.scss"
import "../../assets/scss/Billing/billingsPages.scss"
import { BsCloudDownloadFill, BsCloudUploadFill, BsFillPencilFill, BsXLg, BsDownload } from 'react-icons/bs'
import PopUp from './Common/PopUp/PopUp'
import { Parser } from 'json2csv';
import ProductTable from './Common/PopUp/Products/ProductTable'

const ITEMS_PER_PAGE = 15;

export default function Products() {
    const userContext = useContext(UserContext)
    const [loading, setLoading] = useState(true)
    const [products, setProducts] = useState([])
    const [activeSidebar, setActiveSidebar] = useState(false)
    const [sidebarContent, setSidebarContent] = useState(null)
    const [search, setSearch] = useState("")
    const [showPopUp, setShowPopUp] = useState(false)
    const [action, setAction] = useState("")
    const [popUpContent, setPopUpContent] = useState("")
    const [inactiveProducts, setInactiveProducts] = useState([])
    const [activeProducts, setActiveProducts] = useState([])
    const [activeCurrentPage, setActiveCurrentPage] = useState(1);
    const [inactiveCurrentPage, setInactiveCurrentPage] = useState(1);
    const [productDocument, setProductDocument] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'code', direction: 'asc' });
    const [exportLoading, setExportLoading] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');

    function convertToCSV(data) {
        const fields = [
            { label: 'Code', value: 'code' },
            { label: 'Libellé', value: 'label' },
            { label: 'Famille', value: 'family' },
            { label: 'Type', value: 'type' },
            { label: 'Prix', value: 'price' },
            { label: 'Prix Total', value: 'priceFull' },
            { label: 'Quantité', value: 'quantity' },
            { label: 'TVA', value: 'taxes' },
            { label: 'Compte comptable', value: 'account' },
            { label: 'Description', value: 'description' },
            { label: 'État', value: 'state' }
        ];
        const json2csvParser = new Parser({ fields, withBOM: true });
        return json2csvParser.parse(data);
    }

    function downloadCSV(csv, filename) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    useEffect(() => {
        userContext.apiReducer("getProducts").then(productsList => {
            setLoading(false)
            productsList = sortProducts(productsList, "abc", "code")
            setProducts(productsList)
        })
    }, [userContext])

    useEffect(() => {
        if (products.length > 0) {
            setActiveProducts(products.filter(p => !p.inactive));
            setInactiveProducts(products.filter(p => p.inactive));
        }
    }, [products])

    const handleProductUpdate = (modifiedProduct) => {
        const updatedProducts = products.map(p =>
            p._id === modifiedProduct._id ? modifiedProduct : p
        );
        setProducts(updatedProducts);

        if (modifiedProduct.inactive) {
            setActiveProducts(prev => prev.filter(p => p._id !== modifiedProduct._id));
            setInactiveProducts(prev => [...prev, modifiedProduct]);
        } else {
            setInactiveProducts(prev => prev.filter(p => p._id !== modifiedProduct._id));
            setActiveProducts(prev => [...prev, modifiedProduct]);
        }
    };

    const handleProductAdd = (newProduct) => {
        setProducts(prev => [...prev, newProduct]);
        if (newProduct.inactive) {
            setInactiveProducts(prev => [...prev, newProduct]);
        } else {
            setActiveProducts(prev => [...prev, newProduct]);
        }
    };

    const deleteProduct = async (productId) => {
        try {
            // Delete product logic
            // ...
        } catch (error) {
            console.error("Error deleting product:", error);
            userContext.setError({
                level: 'error',
                message: 'Erreur lors de la suppression du produit',
                btn1Text: 'Fermer',
                btn2hidden: true
            });
        }
    };

    function sortProducts(list, method, key) {
        switch (method) {
            case "abc":
                if (key) {
                    list.sort((a, b) => {
                        // Special handling for code field - sort numerically
                        if (key === "code") {
                            const numA = parseInt(a[key]) || 0;
                            const numB = parseInt(b[key]) || 0;
                            return numA - numB;
                        }

                        // String comparison for other fields
                        if (a[key] < b[key]) return -1;
                        if (a[key] > b[key]) return 1;
                        return 0;
                    });
                }
                break;

            default:
                break;
        }
        return list
    }

    function capitalizeText(text) {
        return text.toLowerCase().replace(/\b\w/g, function (char) {
            return char.toUpperCase();
        });
    }

    const exportProductsToCSV = async () => {
        try {
            setExportLoading(true);

            const dateRangeText = exportStartDate && exportEndDate
                ? `du ${new Date(exportStartDate).toLocaleDateString('fr-FR')} au ${new Date(exportEndDate).toLocaleDateString('fr-FR')}`
                : 'de toutes les périodes';

            userContext.setError({
                level: 'info',
                message: `Récupération des données de facturation ${dateRangeText}...`,
                btn1Text: 'Patienter...',
                btn1hidden: true,
                btn2hidden: true
            });

            const billsResponse = await userContext.apiReducer("getBills", {
                bypassCache: true,
                filterDeleted: true
            });

            const allBills = billsResponse.bills || billsResponse || [];

            let billsToCheck = allBills.filter(bill => bill.documentState !== "Brouillon");

            if (exportStartDate && exportEndDate) {
                const startDate = new Date(exportStartDate);
                const endDate = new Date(exportEndDate);
                endDate.setHours(23, 59, 59, 999);

                billsToCheck = billsToCheck.filter(bill => {
                    const billDate = new Date(bill.date);
                    return billDate >= startDate && billDate <= endDate;
                });
            }

            const productsUsedInPeriod = new Set();

            billsToCheck.forEach(bill => {
                const billProducts = bill.document?.products || bill.billContents || bill.items || [];
                billProducts.forEach(product => {
                    if (product.code) {
                        productsUsedInPeriod.add(String(product.code));
                    }
                    if (product.productCode) {
                        productsUsedInPeriod.add(String(product.productCode));
                    }
                });
            });

            const usageColumnHeader = exportStartDate && exportEndDate
                ? `Utilisé ${dateRangeText}`
                : 'Utilisé en 2025';

            const productsWithUsage = products.map(product => ({
                code: String(product.code).padStart(7, '0'),
                label: product.label || '',
                family: product.family || '',
                type: product.type || '',
                price: product.price || '0',
                priceFull: product.priceFull || '0',
                quantity: product.quantity || '',
                taxes: product.taxes || '',
                account: product.account || '',
                description: product.description || '',
                state: product.state || '',
                usedInPeriod: productsUsedInPeriod.has(String(product.code)) ? 'Oui' : 'Non'
            }));

            const csvContent = productsWithUsage.map(product => {
                return [
                    product.code,
                    `"${product.label.replace(/"/g, '""')}"`,
                    `"${product.family.replace(/"/g, '""')}"`,
                    `"${product.type.replace(/"/g, '""')}"`,
                    `"${product.price.toString().replace('.', ',')}"`,
                    `"${product.priceFull.toString().replace('.', ',')}"`,
                    `"${product.quantity.replace(/"/g, '""')}"`,
                    `"${product.taxes.toString().replace('.', ',')}"`,
                    `"${product.account.replace(/"/g, '""')}"`,
                    `"${product.description.replace(/"/g, '""')}"`,
                    `"${product.state.replace(/"/g, '""')}"`,
                    product.usedInPeriod
                ].join(';');
            });

            const headers = [
                'Code',
                'Libellé',
                'Famille',
                'Type',
                'Prix HT',
                'Prix TTC',
                'Unité',
                'TVA (%)',
                'Compte comptable',
                'Description',
                'État',
                usageColumnHeader
            ].join(';');

            // Add 'sep=;' directive for Excel to recognize semicolon delimiter
            const csvString = ['sep=;', headers, ...csvContent].join('\n');

            // Create blob with proper UTF-8 BOM encoding for French characters
            const BOM = '\uFEFF';
            const encoder = new TextEncoder();
            const csvBytes = encoder.encode(BOM + csvString);
            const blob = new Blob([csvBytes], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            const dateRangeStr = exportStartDate && exportEndDate ?
                `${exportStartDate.replace(/-/g, '')}_${exportEndDate.replace(/-/g, '')}` :
                new Date().toISOString().slice(0, 10).replace(/-/g, '');

            link.setAttribute('href', url);
            link.setAttribute('download', `produits_export_${dateRangeStr}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setExportLoading(false);
            userContext.setError({
                level: 'success',
                message: `Export terminé avec succès pour ${productsWithUsage.length} produits. ${productsUsedInPeriod.size} produits ont été utilisés ${dateRangeText}.`,
                btn1Text: 'OK',
                btn2hidden: true
            });

        } catch (error) {
            console.error("Error exporting products to CSV:", error);
            setExportLoading(false);
            userContext.setError({
                level: 'error',
                message: `Erreur lors de l'export: ${error.message || 'Erreur inconnue'}`,
                btn1Text: 'OK',
                btn2hidden: true
            });
        }
    };

    // Calculate paginated active products
    const filteredActiveProducts = activeProducts?.filter(product =>
        search ? (
            product.label?.toLowerCase().includes(search.toLowerCase()) ||
            product.family?.toLowerCase().includes(search.toLowerCase()) ||
            product.type?.toLowerCase().includes(search.toLowerCase())
        ) : true
    );

    const sortedActiveProducts = (() => {
        const data = [...filteredActiveProducts];
        const { key, direction } = sortConfig || {};
        const dir = direction === 'asc' ? 1 : -1;
        const extractNumericId = (val) => {
            const str = String(val ?? '');
            const digitsOnly = (str.match(/\d+/g)?.join('')) || '';
            if (!digitsOnly) return null;
            const num = parseInt(digitsOnly, 10);
            return Number.isNaN(num) ? null : num;
        };
        const getVal = (p, k) => {
            switch (k) {
                case 'code': return p.code ?? '';
                case 'label': return p.label ?? '';
                case 'family': return p.family ?? '';
                case 'type': return p.type ?? '';
                case 'price': return parseFloat(p.price ?? 0);
                case 'priceFull': return parseFloat(p.priceFull ?? 0);
                default: return '';
            }
        };
        data.sort((a, b) => {
            const va = getVal(a, key);
            const vb = getVal(b, key);
            if (key === 'code') {
                const strA = String(va ?? '').trim();
                const strB = String(vb ?? '').trim();
                const isPureNumA = /^\d+$/.test(strA);
                const isPureNumB = /^\d+$/.test(strB);
                const na = extractNumericId(strA);
                const nb = extractNumericId(strB);

                // Group pure numeric codes before/after alpha based on direction
                if (isPureNumA && !isPureNumB) return -1 * dir;
                if (!isPureNumA && isPureNumB) return 1 * dir;

                if (isPureNumA && isPureNumB && na !== null && nb !== null) {
                    return (na - nb) * dir;
                }
                if (!isPureNumA && !isPureNumB && na !== null && nb !== null) {
                    const numCmp = (na - nb) * dir;
                    if (numCmp !== 0) return numCmp;
                    return strA.localeCompare(strB, 'fr', { numeric: true }) * dir;
                }
                return strA.localeCompare(strB, 'fr', { numeric: true }) * dir;
            }
            if (typeof va === 'number' && typeof vb === 'number') {
                return (va - vb) * dir;
            }
            return String(va).localeCompare(String(vb), 'fr', { numeric: true }) * dir;
        });
        return data;
    })();

    const activeTotalPages = Math.ceil(sortedActiveProducts.length / ITEMS_PER_PAGE);
    const paginatedActiveProducts = sortedActiveProducts.slice(
        (activeCurrentPage - 1) * ITEMS_PER_PAGE,
        activeCurrentPage * ITEMS_PER_PAGE
    );

    // Calculate paginated inactive products
    const filteredInactiveProducts = inactiveProducts?.filter(product =>
        search ? (
            product.label?.toLowerCase().includes(search.toLowerCase()) ||
            product.family?.toLowerCase().includes(search.toLowerCase()) ||
            product.type?.toLowerCase().includes(search.toLowerCase())
        ) : true
    );

    const sortedInactiveProducts = (() => {
        const data = [...filteredInactiveProducts];
        const { key, direction } = sortConfig || {};
        const dir = direction === 'asc' ? 1 : -1;
        const extractNumericId = (val) => {
            const str = String(val ?? '');
            const digitsOnly = (str.match(/\d+/g)?.join('')) || '';
            if (!digitsOnly) return null;
            const num = parseInt(digitsOnly, 10);
            return Number.isNaN(num) ? null : num;
        };
        const getVal = (p, k) => {
            switch (k) {
                case 'code': return p.code ?? '';
                case 'label': return p.label ?? '';
                case 'family': return p.family ?? '';
                case 'type': return p.type ?? '';
                case 'price': return parseFloat(p.price ?? 0);
                case 'priceFull': return parseFloat(p.priceFull ?? 0);
                default: return '';
            }
        };
        data.sort((a, b) => {
            const va = getVal(a, key);
            const vb = getVal(b, key);
            if (key === 'code') {
                const strA = String(va ?? '').trim();
                const strB = String(vb ?? '').trim();
                const isPureNumA = /^\d+$/.test(strA);
                const isPureNumB = /^\d+$/.test(strB);
                const na = extractNumericId(strA);
                const nb = extractNumericId(strB);

                // Group pure numeric codes before/after alpha based on direction
                if (isPureNumA && !isPureNumB) return -1 * dir;
                if (!isPureNumA && isPureNumB) return 1 * dir;

                if (isPureNumA && isPureNumB && na !== null && nb !== null) {
                    return (na - nb) * dir;
                }
                if (!isPureNumA && !isPureNumB && na !== null && nb !== null) {
                    const numCmp = (na - nb) * dir;
                    if (numCmp !== 0) return numCmp;
                    return strA.localeCompare(strB, 'fr', { numeric: true }) * dir;
                }
                return strA.localeCompare(strB, 'fr', { numeric: true }) * dir;
            }
            if (typeof va === 'number' && typeof vb === 'number') {
                return (va - vb) * dir;
            }
            return String(va).localeCompare(String(vb), 'fr', { numeric: true }) * dir;
        });
        return data;
    })();

    const inactiveTotalPages = Math.ceil(sortedInactiveProducts.length / ITEMS_PER_PAGE);
    const paginatedInactiveProducts = sortedInactiveProducts.slice(
        (inactiveCurrentPage - 1) * ITEMS_PER_PAGE,
        inactiveCurrentPage * ITEMS_PER_PAGE
    );

    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'desc' };
        });
    };

    // Reset pagination when search changes
    useEffect(() => {
        setActiveCurrentPage(1);
        setInactiveCurrentPage(1);
    }, [search]);

    if (loading) {
        return (
            <div className={"col cardsContainer mx-1 p-2 mt-3 mt-md-0"}>
                <div className={"row justify-content-between"}>
                    <div>
                        <h1 className="blue fw-bold text-center">Produits</h1>
                    </div>
                </div>
                <Spinner strokeWidth={9} width={40} color={"#003952"} />
                <h2 className="text-center">Chargement des produits...</h2>
            </div>
        )
    }

    return (
        <div className={"col cardsContainer mx-1 p-2 mt-3 mt-md-0 products"}>
            <div className={"row justify-content-between"}>
                <div>
                    <h1 className="blue fw-bold text-center">Produits</h1>
                </div>
            </div>
            <div className="search-and-stuff d-flex justify-content-between align-items-center">
                <div className="search">
                    <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Rechercher un produit..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-outline-success"
                        onClick={() => setShowExportDialog(!showExportDialog)}
                        disabled={products.length === 0}
                    >
                        <BsDownload className="me-2" />
                        Export {showExportDialog ? '▲' : '▼'}
                    </button>
                    <button
                        className="btn btn-primary nice-button"
                        onClick={() => {
                            setShowPopUp(true);
                            setAction("addProduct");
                            setPopUpContent({ what: { type: "Produit", title: "Créer un produit" }, thing: { object: {} } });
                        }}
                    >+</button>
                </div>
                <br />

            </div>

            {/* Export dialog section */}
            {showExportDialog && (
                <div className="filters-panel mt-3 p-3 border rounded bg-light mb-3">
                    <div className="row align-items-end">
                        <div className="col-12">
                            <h5>Exporter les produits</h5>
                            <p className="small text-muted">
                                Sélectionnez une plage de dates pour vérifier l'utilisation des produits dans la facturation.
                                Laissez vide pour voir l'utilisation sur toute l'année 2025.
                            </p>
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Du</label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportStartDate}
                                onChange={(e) => setExportStartDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Au</label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportEndDate}
                                onChange={(e) => setExportEndDate(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4 mb-3">
                            <button
                                className="btn btn-success w-100"
                                onClick={exportProductsToCSV}
                                disabled={exportLoading}
                            >
                                {exportLoading ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                ) : (
                                    <BsDownload className="me-2" />
                                )}
                                Exporter en CSV
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="active-products mb-5">
                <h3 className="blue mt-4">Produits Actifs ({filteredActiveProducts.length})</h3>

                <table className='table-maxxing'>
                    <thead>
                        <tr>
                            <th scope="col" onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}><span>Code{sortConfig.key === 'code' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('label')} style={{ cursor: 'pointer' }}><span>Libellé{sortConfig.key === 'label' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('family')} style={{ cursor: 'pointer' }}><span>Famille{sortConfig.key === 'family' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}><span>Type{sortConfig.key === 'type' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}><span>Prix HT{sortConfig.key === 'price' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('priceFull')} style={{ cursor: 'pointer' }}><span>Prix TTC{sortConfig.key === 'priceFull' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col"><span>Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedActiveProducts.map((product, index) => (
                            <tr key={product._id || index}>
                                <td>{String(product.code).padStart(7, '0')}</td>
                                <td><strong>{product.label}</strong></td>
                                <td>{product.family}</td>
                                <td>{product.type}</td>
                                <td>{parseFloat(product.price || 0).toFixed(2)}€</td>
                                <td>{parseFloat(product.priceFull || 0).toFixed(2)}€</td>
                                <td>
                                    <div className="d-flex justify-content-end">
                                        <button
                                            className="btn btn-sm btn-outline-primary mx-1"
                                            title="Modifier"
                                            onClick={() => {
                                                setShowPopUp(true);
                                                setAction("modifyProduct");
                                                setPopUpContent({ what: { type: "Produit", title: "Modifier un produit" }, thing: { object: product } });
                                            }}
                                        >
                                            <BsFillPencilFill className="billsActionsIcon" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredActiveProducts.length > 0 && (
                    <div className="pagination d-flex justify-content-center align-items-center gap-2 mt-3">
                        <button
                            className="btn btn-primary"
                            disabled={activeCurrentPage === 1}
                            onClick={() => setActiveCurrentPage(prev => Math.max(1, prev - 1))}
                        >
                            Précédent
                        </button>
                        <span>Page {activeCurrentPage} of {activeTotalPages}</span>
                        <button
                            className="btn btn-primary"
                            disabled={activeCurrentPage === activeTotalPages}
                            onClick={() => setActiveCurrentPage(prev => Math.min(activeTotalPages, prev + 1))}
                        >
                            Suivant
                        </button>
                    </div>
                )}
            </div>

            <div className="inactive-products">
                <h3 className="blue mt-4">Produits Inactifs ({filteredInactiveProducts.length})</h3>
                <table className='table-maxxing'>
                    <thead>
                        <tr>
                            <th scope="col" onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}><span>Code{sortConfig.key === 'code' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('label')} style={{ cursor: 'pointer' }}><span>Libellé{sortConfig.key === 'label' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('family')} style={{ cursor: 'pointer' }}><span>Famille{sortConfig.key === 'family' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}><span>Type{sortConfig.key === 'type' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}><span>Prix HT{sortConfig.key === 'price' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col" onClick={() => handleSort('priceFull')} style={{ cursor: 'pointer' }}><span>Prix TTC{sortConfig.key === 'priceFull' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</span></th>
                            <th scope="col"><span>Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedInactiveProducts.map((product, index) => (
                            <tr key={product._id || index}>
                                <td>{product.code}</td>
                                <td>{product.label}</td>
                                <td>{product.family}</td>
                                <td>{product.type}</td>
                                <td>{parseFloat(product.price || 0).toFixed(2)}€</td>
                                <td>{parseFloat(product.priceFull || 0).toFixed(2)}€</td>
                                <td>
                                    <div className="d-flex justify-content-end">
                                        <button
                                            className="btn btn-sm btn-outline-primary mx-1"
                                            title="Modifier"
                                            onClick={() => {
                                                setShowPopUp(true);
                                                setAction("modifyProduct");
                                                setPopUpContent({ what: { type: "Produit", title: "Modifier un produit" }, thing: { object: product } });
                                            }}
                                        >
                                            <BsFillPencilFill className="billsActionsIcon" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredInactiveProducts.length > 0 && (
                    <div className="pagination d-flex justify-content-center align-items-center gap-2 mt-3">
                        <button
                            className="btn btn-primary"
                            disabled={inactiveCurrentPage === 1}
                            onClick={() => setInactiveCurrentPage(prev => Math.max(1, prev - 1))}
                        >
                            Précédent
                        </button>
                        <span>Page {inactiveCurrentPage} of {inactiveTotalPages}</span>
                        <button
                            className="btn btn-primary"
                            disabled={inactiveCurrentPage === inactiveTotalPages}
                            onClick={() => setInactiveCurrentPage(prev => Math.min(inactiveTotalPages, prev + 1))}
                        >
                            Suivant
                        </button>
                    </div>
                )}
            </div>

            <div id='product-sidebar' className={'col' + (activeSidebar ? " active" : "")}>
                <div className='header'>
                    <button className="close-sidebar" onClick={() => setActiveSidebar(false)}><BsXLg /></button>
                    <button className="edit-sidebar" onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowPopUp(true)
                        setAction("modifyProduct")
                        const product = { ...sidebarContent.thing }
                        setPopUpContent({ what: { type: "Produits", title: "Modifier un produit" }, thing: { product } })
                    }}><BsFillPencilFill /></button>


                    {sidebarContent ? <h2>{sidebarContent.what}</h2> : ""}
                </div>
                {sidebarContent ?
                    <div className='content'>
                        <div className='row'>
                            <div className='col-12'>
                                <h2>{sidebarContent.thing.code + " - " + sidebarContent.thing.label}</h2>
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12'>
                                <ul>
                                    <li><span>Description : </span>{sidebarContent.thing.description}</li>
                                    <li><span>Type :</span> {capitalizeText(sidebarContent.thing.type)}</li>
                                    <li><span>Famille :</span> {sidebarContent.thing.family}</li>
                                    <li><span>Prix HT :</span> {sidebarContent.thing.price}€</li>
                                    <li><span>Prix TTC :</span> {sidebarContent.thing.priceFull}€</li>
                                    <li><span>TVA :</span> {sidebarContent.thing.taxes}% </li>
                                    <li><span>Unité :</span> {sidebarContent.thing.quantity}</li>
                                    <li><span>Compte comptable :</span> {sidebarContent.thing.account}</li>
                                    <li><span>Actif :</span> {sidebarContent.thing.state === "ACTIF" ? "Oui" : "Non"}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    : ""}
            </div>
            <PopUp
                showPopUp={showPopUp}
                setShowPopUp={setShowPopUp}
                action={action}
                popUpContent={popUpContent}
                type={"product"}
                setProducts={setProducts}
                handleProductUpdate={handleProductUpdate}
                document={productDocument}
                setDocument={setProductDocument}
                userContext={userContext}
                setReference={() => { }}
                setSiret={() => { }}
                setAddress={() => { }}
                setPayingMethod={() => { }}
                setBillingAddress={() => { }}
                setFullAddress={() => { }}
                setFullSecondAddress={() => { }}
                setIsTheSameAsFullAddress={() => { }}
                setFullName={() => { }}
                setClientMail={() => { }}
                setClientId={() => { }}
                setBillContents={() => { }}
                billContents={[]}
                reference=""
                siret=""
                address=""
                payingMethod=""
                billingAddress={{}}
                fullAddress=""
                fullSecondAddress=""
                isTheSameAsFullAddress={false}
                fullName=""
                clientMail=""
                clientId=""
                onUpdate={() => { }}
            />
        </div>
    )
}